import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { FaChevronDown, FaUser, FaSignOutAlt } from "react-icons/fa"
import {
  Bell,
  FileText,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  UserMinus,
  Video,
  Bot,
} from "lucide-react"
import { Avatar, AvatarFallback } from "@/modules/shared/ui/components/avatar"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Progress } from "@/modules/shared/ui/components/progress"
import { ThemeToggle } from "@/modules/shared/ui/components/theme-toggle"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { AppRole } from "@/modules/auth/constants/app-role"
import { useEmployeeOnboarding } from "@/modules/employee/employee/hooks/employeeHook"
import { useCvUploadContext } from "@/modules/recruitment/context/CvUploadContext"
import { useDocPushContext } from "@/modules/sage300/context/DocPushContext"
import { useBulkRejectionContext } from "@/modules/recruitment/context/BulkRejectionContext"
import { useBulkVideoContext } from "@/modules/recruitment/context/BulkVideoContext"
import { useBulkAiCallContext } from "@/modules/recruitment/context/BulkAiCallContext"

/**
 * Single top navbar for all authenticated routes.
 *
 * - Employee role  → welcome banner with the employee's name
 * - All other roles → user avatar dropdown (profile link + sign-out)
 */
export default function AuthenticatedNavbar() {
  const { user, logout, hasRole } = useAuth()
  const isEmployee = hasRole(AppRole.Employee)

  if (isEmployee) {
    return <EmployeeNavbarContent />
  }

  return <OrgNavbarContent user={user} logout={logout} />
}

// ── Employee variant ───────────────────────────────────────────────────────

function EmployeeNavbarContent() {
  const { user, logout } = useAuth()
  const { onboardingData } = useEmployeeOnboarding()
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const userDropdownRef = useRef<HTMLDivElement>(null)

  const displayName =
    `${onboardingData?.firstName ?? ""} ${onboardingData?.lastName ?? ""}`.trim() ||
    `${user?.user.firstName ?? ""} ${user?.user.lastName ?? ""}`.trim() ||
    "Employee"

  const initials =
    `${(onboardingData?.firstName ?? user?.user.firstName ?? "")[0] ?? ""}${(onboardingData?.lastName ?? user?.user.lastName ?? "")[0] ?? ""}`
      .trim()
      .toUpperCase() || "E"

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <header className="bg-card border-b border-border px-4 py-2.5 flex items-center justify-between gap-2 shadow-sm shrink-0">
      <div className="hidden md:block px-2">
        <p className="text-sm font-semibold text-foreground">
          {displayName ? `Welcome, ${displayName}` : "Welcome to Tempered Talent"}
        </p>
      </div>

      <div className="flex items-center gap-2 ml-auto">
        <ThemeToggle />

        {/* User avatar dropdown */}
        <div className="relative" ref={userDropdownRef}>
          <button
            type="button"
            onClick={() => setShowUserDropdown((prev) => !prev)}
            className="flex items-center gap-2 rounded-lg hover:bg-muted p-1 transition-colors cursor-pointer"
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden md:block text-sm font-medium text-foreground max-w-[120px] truncate">
              {displayName}
            </span>
            <FaChevronDown
              className={`h-3 w-3 text-muted-foreground hidden md:block transition-transform ${showUserDropdown ? "rotate-180" : ""}`}
            />
          </button>

          {showUserDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-muted">
                <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.user.email ?? ""}</p>
              </div>
              <div className="py-1">
                <Link
                  to="/account/profile"
                  onClick={() => setShowUserDropdown(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <FaUser className="h-4 w-4 text-muted-foreground" />
                  My Profile
                </Link>
              </div>
              <div className="border-t border-border py-1">
                <button
                  onClick={() => { setShowUserDropdown(false); logout() }}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-destructive transition-colors w-full text-left"
                >
                  <FaSignOutAlt className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

// ── Org variant ────────────────────────────────────────────────────────────

interface OrgNavbarContentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any
  logout: () => void
}

function OrgNavbarContent({ user, logout }: OrgNavbarContentProps) {
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [showBellPanel, setShowBellPanel] = useState(false)
  const userDropdownRef = useRef<HTMLDivElement>(null)
  const bellRef = useRef<HTMLDivElement>(null)
  const {
    uploads,
    activeCount: cvActiveCount,
    clearCompleted: clearCompletedUploads,
  } = useCvUploadContext()
  const {
    pushes,
    activeCount: docPushActiveCount,
    clearCompleted: clearCompletedPushes,
  } = useDocPushContext()
  const {
    jobs: bulkRejectionJobs,
    activeCount: bulkRejectionActiveCount,
    clearCompleted: clearCompletedBulkRejections,
  } = useBulkRejectionContext()
  const {
    jobs: bulkVideoJobs,
    activeCount: bulkVideoActiveCount,
    clearCompleted: clearCompletedBulkVideoJobs,
  } = useBulkVideoContext()
  const {
    jobs: bulkAiCallJobs,
    activeCount: bulkAiCallActiveCount,
    clearCompleted: clearCompletedBulkAiCalls,
  } = useBulkAiCallContext()

  const activeCount =
    cvActiveCount + docPushActiveCount + bulkRejectionActiveCount + bulkVideoActiveCount + bulkAiCallActiveCount

  const clearCompleted = () => {
    clearCompletedUploads()
    clearCompletedPushes()
    clearCompletedBulkRejections()
    clearCompletedBulkVideoJobs()
    clearCompletedBulkAiCalls()
  }

  const displayName =
    `${user?.user.firstName ?? ""} ${user?.user.lastName ?? ""}`.trim() ||
    "User"
  const initials =
    `${user?.user.firstName?.[0] ?? ""}${user?.user.lastName?.[0] ?? ""}`
      .trim()
      .toUpperCase() || "U"

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false)
      }
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setShowBellPanel(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const hasCompleted =
    uploads.some((u) => u.status === "Completed" || u.status === "Failed") ||
    pushes.some((p) => p.status === "Completed" || p.status === "Failed") ||
    bulkRejectionJobs.some((j) => j.status === "Completed" || j.status === "Failed") ||
    bulkVideoJobs.some((j) => j.status === "Completed" || j.status === "Failed") ||
    bulkAiCallJobs.some((j) => j.status === "Completed" || j.status === "Failed")

  return (
    <header className="bg-card border-b border-border px-4 py-2.5 flex items-center justify-end gap-2 shadow-sm shrink-0">
      {/* Theme toggle */}
      <ThemeToggle />

      {/* Bell notification */}
      <div className="relative" ref={bellRef}>
        <button
          onClick={() => setShowBellPanel((prev) => !prev)}
          className="relative flex items-center justify-center h-8 w-8 rounded-lg hover:bg-muted transition-colors cursor-pointer"
          aria-label="Upload notifications"
        >
          <Bell className="h-4.5 w-4.5 text-muted-foreground" />
          {activeCount > 0 && (
            <span className="absolute top-0.5 right-0.5 flex items-center justify-center h-4 w-4 rounded-full bg-destructive text-white text-[9px] font-bold leading-none">
              {activeCount > 9 ? "9+" : activeCount}
            </span>
          )}
        </button>

        {showBellPanel && (
          <div className="absolute right-0 mt-2 w-80 bg-popover border border-border rounded-xl shadow-lg z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted">
              <p className="text-sm font-semibold text-foreground">
                Notifications
              </p>
              {hasCompleted && (
                <button
                  onClick={clearCompleted}
                  className="text-xs text-primary font-medium transition-colors"
                >
                  Clear completed
                </button>
              )}
            </div>

            {uploads.length === 0 && pushes.length === 0 && bulkRejectionJobs.length === 0 && bulkVideoJobs.length === 0 && bulkAiCallJobs.length === 0 ? (
              <p className="px-4 py-4 text-sm text-muted-foreground text-center">
                Nothing to show yet.
              </p>
            ) : (
              <ul className="max-h-72 overflow-y-auto divide-y divide-border/40">
                {uploads.map((u) => (
                  <li
                    key={u.correlationId}
                    className="px-4 py-3 flex flex-col gap-1.5"
                  >
                    <div className="flex items-center gap-2">
                      {u.status === "Pending" && (
                        <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      )}
                      {u.status === "Processing" && (
                        <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin shrink-0" />
                      )}
                      {u.status === "Completed" && (
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                      )}
                      {u.status === "Failed" && (
                        <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      )}
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span
                        className="flex-1 text-xs text-foreground truncate"
                        title={u.fileName}
                      >
                        {u.fileName}
                      </span>
                      <Badge
                        variant={
                          u.status === "Completed"
                            ? "default"
                            : u.status === "Failed"
                              ? "destructive"
                              : u.status === "Processing"
                                ? "outline"
                                : "secondary"
                        }
                        className="text-[10px] px-1.5 py-0 shrink-0"
                      >
                        {u.status === "Pending" ? "Queued" : u.status}
                      </Badge>
                    </div>
                    {(u.status === "Pending" || u.status === "Processing") && (
                      <Progress
                        value={
                          u.status === "Processing" ? 70 : (u.progress ?? 0)
                        }
                        className={`h-1 transition-all${u.status === "Processing" ? " animate-pulse" : ""}`}
                      />
                    )}
                    {u.status === "Completed" && (
                      <Progress value={100} className="h-1" />
                    )}
                    {u.status === "Failed" && u.errorMessage && (
                      <p className="text-xs text-destructive">{u.errorMessage}</p>
                    )}
                  </li>
                ))}
                {pushes.map((p) => (
                  <li
                    key={p.pushId}
                    className="px-4 py-3 flex flex-col gap-1.5"
                  >
                    <div className="flex items-center gap-2">
                      {p.status === "Queued" && (
                        <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      )}
                      {p.status === "Processing" && (
                        <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin shrink-0" />
                      )}
                      {p.status === "Completed" && (
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                      )}
                      {p.status === "Failed" && (
                        <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      )}
                      <Send className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span
                        className="flex-1 text-xs text-foreground truncate"
                        title={p.label}
                      >
                        {p.label}
                      </span>
                      <Badge
                        variant={
                          p.status === "Completed"
                            ? "default"
                            : p.status === "Failed"
                              ? "destructive"
                              : p.status === "Processing"
                                ? "outline"
                                : "secondary"
                        }
                        className="text-[10px] px-1.5 py-0 shrink-0"
                      >
                        {p.status}
                      </Badge>
                    </div>
                    {(p.status === "Queued" || p.status === "Processing") && (
                      <Progress
                        value={p.status === "Processing" ? 70 : 0}
                        className={`h-1 transition-all${p.status === "Processing" ? " animate-pulse" : ""}`}
                      />
                    )}
                    {p.status === "Completed" && (
                      <Progress value={100} className="h-1" />
                    )}
                    {p.status === "Failed" && p.errorMessage && (
                      <p className="text-xs text-destructive">{p.errorMessage}</p>
                    )}
                  </li>
                ))}
                {bulkRejectionJobs.map((j) => (
                  <li key={j.jobId} className="px-4 py-3 flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      {(j.status === "Pending" || j.status === "InProgress") && (
                        <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin shrink-0" />
                      )}
                      {j.status === "Completed" && (
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                      )}
                      {j.status === "Failed" && (
                        <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      )}
                      <UserMinus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="flex-1 text-xs text-foreground truncate">
                        Bulk rejection ({j.total} candidate{j.total !== 1 ? "s" : ""})
                      </span>
                      <Badge
                        variant={
                          j.status === "Completed"
                            ? "default"
                            : j.status === "Failed"
                              ? "destructive"
                              : "outline"
                        }
                        className="text-[10px] px-1.5 py-0 shrink-0"
                      >
                        {j.status === "Pending" ? "Queued" : j.status === "InProgress" ? "Processing" : j.status}
                      </Badge>
                    </div>
                    {(j.status === "Pending" || j.status === "InProgress") && (
                      <Progress
                        value={j.total > 0 ? Math.round((j.processed / j.total) * 100) : 0}
                        className="h-1 transition-all"
                      />
                    )}
                    {j.status === "Completed" && (
                      <Progress value={100} className="h-1" />
                    )}
                  </li>
                ))}
                {bulkVideoJobs.map((j) => (
                  <li key={j.jobId} className="px-4 py-3 flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      {(j.status === "Pending" || j.status === "InProgress") && (
                        <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin shrink-0" />
                      )}
                      {j.status === "Completed" && (
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                      )}
                      {j.status === "Failed" && (
                        <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      )}
                      <Video className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="flex-1 text-xs text-foreground truncate">
                        Video interviews ({j.total} candidate{j.total !== 1 ? "s" : ""})
                      </span>
                      <Badge
                        variant={
                          j.status === "Completed"
                            ? "default"
                            : j.status === "Failed"
                              ? "destructive"
                              : "outline"
                        }
                        className="text-[10px] px-1.5 py-0 shrink-0"
                      >
                        {j.status === "Pending" ? "Queued" : j.status === "InProgress" ? "Processing" : j.status}
                      </Badge>
                    </div>
                    {(j.status === "Pending" || j.status === "InProgress") && (
                      <Progress
                        value={j.total > 0 ? Math.round((j.processed / j.total) * 100) : 0}
                        className="h-1 transition-all"
                      />
                    )}
                    {j.status === "Completed" && (
                      <Progress value={100} className="h-1" />
                    )}
                  </li>
                ))}
                {bulkAiCallJobs.map((j) => (
                  <li key={j.jobId} className="px-4 py-3 flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      {(j.status === "Pending" || j.status === "InProgress") && (
                        <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin shrink-0" />
                      )}
                      {j.status === "Completed" && (
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                      )}
                      {j.status === "Failed" && (
                        <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      )}
                      <Bot className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="flex-1 text-xs text-foreground truncate">
                        AI screening calls ({j.total} candidate{j.total !== 1 ? "s" : ""})
                      </span>
                      <Badge
                        variant={
                          j.status === "Completed"
                            ? "default"
                            : j.status === "Failed"
                              ? "destructive"
                              : "outline"
                        }
                        className="text-[10px] px-1.5 py-0 shrink-0"
                      >
                        {j.status === "Pending" ? "Queued" : j.status === "InProgress" ? "Processing" : j.status}
                      </Badge>
                    </div>
                    {(j.status === "Pending" || j.status === "InProgress") && (
                      <Progress
                        value={j.total > 0 ? Math.round((j.processed / j.total) * 100) : 0}
                        className="h-1 transition-all"
                      />
                    )}
                    {j.status === "Completed" && (
                      <Progress value={100} className="h-1" />
                    )}
                    {j.candidates.length > 0 && (
                      <ul className="mt-1 space-y-1 border-l border-border pl-2">
                        {j.candidates.map((c) => (
                          <li
                            key={c.applicationId}
                            className="flex items-center gap-1.5"
                            title={c.failureReason ?? undefined}
                          >
                            {c.status === "Queued" && (
                              <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
                            )}
                            {c.status === "Dispatched" && (
                              <CheckCircle className="h-3 w-3 text-green-500 shrink-0" />
                            )}
                            {c.status === "Failed" && (
                              <XCircle className="h-3 w-3 text-destructive shrink-0" />
                            )}
                            <span className="flex-1 text-[11px] text-muted-foreground truncate">
                              {c.candidateName}
                            </span>
                            <span
                              className={`text-[10px] shrink-0 ${
                                c.status === "Failed"
                                  ? "text-destructive"
                                  : c.status === "Dispatched"
                                    ? "text-green-600 dark:text-green-500"
                                    : "text-muted-foreground"
                              }`}
                            >
                              {c.status}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* User avatar dropdown */}
      <div className="relative" ref={userDropdownRef}>
        <button
          onClick={() => setShowUserDropdown((prev) => !prev)}
          className="flex items-center gap-2 rounded-lg hover:bg-muted p-1 transition-colors cursor-pointer"
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="hidden md:block text-sm font-medium text-foreground max-w-[120px] truncate">
            {displayName}
          </span>
          <FaChevronDown
            className={`h-3 w-3 text-muted-foreground hidden md:block transition-transform ${
              showUserDropdown ? "rotate-180" : ""
            }`}
          />
        </button>

        {showUserDropdown && (
          <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-xl shadow-lg z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-muted">
              <p className="text-sm font-semibold text-foreground truncate">
                {displayName}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.user.email ?? ""}
              </p>
            </div>
            <div className="py-1">
              <Link
                to="/account/profile"
                onClick={() => setShowUserDropdown(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <FaUser className="h-4 w-4 text-muted-foreground" />
                My Profile
              </Link>
            </div>
            <div className="border-t border-border py-1">
              <button
                onClick={() => {
                  setShowUserDropdown(false)
                  logout()
                }}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-destructive transition-colors w-full text-left"
              >
                <FaSignOutAlt className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
